import getApiClient from '.'

/**
 * Get list of jobs with pagination and filters
 * @param {object} params - Query parameters
 * @param {number} params.page - Page number
 * @param {number} params.limit - Items per page
 * @param {string} params.status - Filter by status
 * @param {string} params.type - Filter by type
 * @param {number} params.chainId - Filter by chain ID
 * @param {string} params.search - Search by job name
 * @returns {Promise<object>} Jobs list with pagination info
 */
export const getJobs = async (params = {}) => {
  try {
    const client = await getApiClient()
    // Filter out null/undefined values from params
    const filteredParams = Object.entries(params).reduce((acc, [key, value]) => {
      if (value !== null && value !== undefined) {
        acc[key] = value
      }
      return acc
    }, {})
    const queryParams = new URLSearchParams(filteredParams).toString()
    const response = await client.get(`/api/jobs?${queryParams}`)
    return response.data
  } catch (error) {
    throw new Error(error.response?.data?.error || 'Failed to fetch jobs')
  }
}

/**
 * Get job details including events
 * @param {number} id - Job ID
 * @returns {Promise<object>} Job details with events
 */
export const getJobDetails = async (id) => {
  try {
    const client = await getApiClient()
    const response = await client.get(`/api/jobs/${id}`)
    return response.data
  } catch (error) {
    throw new Error(error.response?.data?.error || 'Failed to fetch job details')
  }
}

/**
 * Get list of job chains with their jobs
 * @param {object} params - Query parameters
 * @param {number} params.page - Page number
 * @param {number} params.limit - Items per page
 * @param {string} params.status - Filter by status
 * @param {string} params.type - Filter by chain type
 * @returns {Promise<object>} Job chains list with pagination info
 */
export const getJobChains = async (params = {}) => {
  try {
    const client = await getApiClient()
    const filteredParams = Object.entries(params).reduce((acc, [key, value]) => {
      if (value !== null && value !== undefined) {
        acc[key] = value
      }
      return acc
    }, {})
    const queryParams = new URLSearchParams(filteredParams).toString()
    const response = await client.get(`/api/jobs/chains?${queryParams}`)
    return response.data
  } catch (error) {
    throw new Error(error.response?.data?.error || 'Failed to fetch job chains')
  }
}

/**
 * Get the latest job chain for the shop
 * @param {object} params - Query parameters
 * @param {string} params.type - Chain type filter (e.g., 'shop_install')
 * @param {string} params.status - Chain status filter
 * @returns {Promise<object>} Latest job chain with jobs
 */
export const getLatestJobChain = async (params = {}) => {
  try {
    const client = await getApiClient()
    const filteredParams = Object.entries(params).reduce((acc, [key, value]) => {
      if (value !== null && value !== undefined) {
        acc[key] = value
      }
      return acc
    }, {})
    const queryParams = new URLSearchParams(filteredParams).toString()
    const response = await client.get(`/api/jobs/chains/latest?${queryParams}`)
    return response.data
  } catch (error) {
    throw new Error(error.response?.data?.error || 'Failed to fetch latest job chain')
  }
}

/**
 * Trigger job execution
 * @param {number} id - Job ID
 * @returns {Promise<object>} Trigger result
 */
export const triggerJob = async (id) => {
  try {
    const client = await getApiClient()
    const response = await client.post(`/api/jobs/${id}/trigger`)
    return response.data
  } catch (error) {
    throw new Error(error.response?.data?.error || 'Failed to trigger job')
  }
}